/**
 * Ed25519 device identity via WebCrypto.
 *
 * The private key is generated non-extractable, so page JS can sign with it but
 * cannot read it out -- the closest a browser gets to the platform keystore that
 * §9 asks for on a native handset.
 *
 * Signatures cover the **exact payload bytes that get transmitted**, carried
 * alongside as base64. That is deliberate: `JSON.stringify` and Python's
 * `json.dumps` do not agree on every float, so any scheme where both sides
 * re-derive a canonical form has a latent cross-language bug. The server verifies
 * the bytes it received and only then parses them.
 */

import { CLAIM_DOMAIN } from "./const.js";

const ALGORITHM = { name: "Ed25519" };

/** Whether this browser can sign at all. Checked before enrollment, not after. */
export async function isSupported() {
  try {
    const kp = await crypto.subtle.generateKey(ALGORITHM, false, ["sign", "verify"]);
    return !!kp.privateKey;
  } catch {
    return false;
  }
}

export async function generateKeypair() {
  // extractable: false -- the private key never becomes bytes anywhere.
  return crypto.subtle.generateKey(ALGORITHM, false, ["sign", "verify"]);
}

export async function publicKeyBase64(keypair) {
  const raw = await crypto.subtle.exportKey("raw", keypair.publicKey);
  return bytesToBase64(new Uint8Array(raw));
}

/**
 * Domain separation for the continuous claim-delta ledger (#169/#170).
 *
 * Deliberately *not* in `const.js` next to `CLAIM_DOMAIN`, and the reason is the same
 * reason the two domains exist at all: a signature is only meaningful against exactly
 * one domain, and the one thing that must never happen is a claim bundle's signature
 * verifying as a claim delta or the reverse. Keeping this string in the module that
 * uses it means there is one file, not two, where the two byte strings can be confused
 * for each other, and it keeps `const.js` -- which the legacy path shares -- untouched.
 *
 * Must equal `crypto.CLAIM_DELTA_DOMAIN` byte for byte, trailing NUL included.
 */
export const CLAIM_DELTA_DOMAIN = "meetfolks/claim-delta/v1\u0000";

/** Sign `payloadObject`'s exact serialised bytes under one domain. */
async function signUnderDomain(keypair, payloadObject, domainString) {
  const json = JSON.stringify(payloadObject);
  const payloadBytes = new TextEncoder().encode(json);
  const domain = new TextEncoder().encode(domainString);

  const message = new Uint8Array(domain.length + payloadBytes.length);
  message.set(domain, 0);
  message.set(payloadBytes, domain.length);

  const sig = await crypto.subtle.sign(ALGORITHM, keypair.privateKey, message);
  return {
    payloadBytes,
    payload: bytesToBase64(payloadBytes),
    signature: bytesToBase64(new Uint8Array(sig)),
  };
}

/**
 * Serialise, sign, and return `{payload, signature}` both base64.
 * The caller transmits both verbatim.
 */
export async function signPayload(keypair, payloadObject) {
  const { payload, signature } = await signUnderDomain(keypair, payloadObject, CLAIM_DOMAIN);
  return { payload, signature };
}

/**
 * Sign one continuous claim delta, and report the digest of the bytes that were signed.
 *
 * The digest is not decoration and it is not derivable later without care: the resume
 * handshake identifies the last acknowledged delta by `(sequence, digest)`, and the
 * server computes that digest as SHA-256 over *the exact payload bytes it received*.
 * Returning it from the one place that has those bytes in hand is what stops a client
 * re-deriving it from a re-serialised object -- which would differ on any float
 * `JSON.stringify` renders differently the second time, and would then fail every
 * resume with `digest_mismatch` forever, on a delta the server had already committed.
 *
 * Lower-case hex, matching `hashlib.sha256(...).hexdigest()` and the 64-hex pattern the
 * resume route validates against.
 */
export async function signClaimDeltaPayload(keypair, payloadObject) {
  const { payloadBytes, payload, signature } = await signUnderDomain(
    keypair,
    payloadObject,
    CLAIM_DELTA_DOMAIN
  );
  const digestBuffer = await crypto.subtle.digest("SHA-256", payloadBytes);
  const digest = Array.from(new Uint8Array(digestBuffer), (b) =>
    b.toString(16).padStart(2, "0")
  ).join("");
  return { payload, signature, digest };
}

export function bytesToBase64(bytes) {
  let s = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(s);
}

/** 16 random bytes as hex. A nonce is good once per session (§9 replay guard). */
export function newNonce() {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}
